import { Lock01, Mail01, PhoneCall01, Truck01 } from '@untitled-ui/icons-react/build/cjs';
import React, { useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import WalLogoIcon from 'src/assets/img/logo/logo-icon.svg';
import ButtonCmp from 'src/components/ButtonCmp';
import CardCmp from 'src/components/CardCmp';
import NotFoundUI from 'src/components/NotFoundUI';
import TooltipCmp from 'src/components/TooltipCmp';
import TruncateTextCmp from 'src/components/TruncateText';
import { CURRENCY } from 'src/constants/common';
import { ROUTES } from 'src/constants/routes';
import useOrgBranding from 'src/hooks/useOrgBranding';
import { currentUser as currentUserSelector } from 'src/redux/auth.slice';
import { orderDetailsData, orderDetailsLoading, selectCurrentLegData, selectOrderLegActiveTab } from 'src/redux/orders.slice';
import { RootState } from 'src/redux/store';
import { extractMcNumber, getFormattedNumber, getShortName, onError, usePhone } from 'src/utils/CommonFunctions';
import WalToast from 'src/utils/WalToast';

const ChargeLabelCell = ({ label }: { label: string }) => {
    const ref = useRef<HTMLParagraphElement>(null);

    return (
        <TruncateTextCmp refName={ref} message={label}>
            <p className="text-xs text-textSecondary font-normal truncate max-w-[280px]" ref={ref}>
                {label}
            </p>
        </TruncateTextCmp>
    );
};

interface ICarrierAssignmentProps {
    isReadOnly?: boolean;
}

const CarrierAssignment = ({ isReadOnly = false }: ICarrierAssignmentProps) => {
    const { t } = useTranslation();
    const orgBranding = useOrgBranding();
    const { getFormatPhoneWithPlus } = usePhone();
    const phoneRef = useRef<HTMLParagraphElement>(null);
    const emailRef = useRef<HTMLParagraphElement>(null);
    const nameRef = useRef<HTMLParagraphElement>(null);
    const additionalsRef = useRef<HTMLSpanElement>(null);
    const [showCharges, setShowCharges] = useState(false);
    const [chargesRect, setChargesRect] = useState<{ bottom: number; top: number; left: number } | null>(null);
    const [chargesAbove, setChargesAbove] = useState(false);
    // Delay closing so the mouse can move from trigger to tooltip without it disappearing.
    const chargesCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const loading = useSelector(orderDetailsLoading);
    const currentLegData = useSelector(selectCurrentLegData);
    const order = useSelector(orderDetailsData);
    const orderLegActiveTab = useSelector(selectOrderLegActiveTab);
    const currentUser = useSelector(currentUserSelector);
    const servicesList = useSelector((state: RootState) => state.basic.servicesList);

    // ── Carrier (left panel) ─────────────────────────────────────────────────
    // Always read currentLegData — the SAME source the progress card (OrderStatus) uses.
    // Both cards sit on this page, so they must show the same carrier: on the master tab
    // currentLegData is leg 1, on a leg tab it is that leg. Covered legs carry the source
    // leg's carrier (flagged isCoveredCarrier by the backend) — presented with no special
    // badge: the customer just sees the carrier moving that leg.
    const isMasterTab = !!orderLegActiveTab?.isMaster;
    const carrierPrice = currentLegData?.carrierPrice;
    const carrier = carrierPrice?.carrier;

    // On share-link (isReadOnly), there is no logged-in user — read salesRep
    // contact details from the order object returned by the API instead.
    const salesRep = isReadOnly
        ? {
              salesrepname: [order?.salesRepFirstName, order?.salesRepLastName].filter(Boolean).join(' ') || null,
              phone: (order as any)?.salesRepPhone || null,
              salesrepemail: order?.salesRepEmail || null,
          }
        : currentUser?.salesRep;

    const handleCopyPhone = () => {
        navigator.clipboard.writeText(salesRep?.phone ?? '');
        WalToast.copy(t('Common.Copied_to_clipboard'), '', 'copy-msg');
    };

    const handleCopyEmail = () => {
        navigator.clipboard.writeText(salesRep?.salesrepemail ?? '');
        WalToast.copy(t('Common.Copied_to_clipboard'), '', 'copy-msg');
    };

    // Carrier identity states — MUST stay in sync with the Overview tab's progress card
    // header (OrderStatus/index.tsx). Both cards sit on the same page, so a carrier that
    // is hidden/absent has to read identically in both places.
    //   isNoCarrierAssigned  → no carrier at all, OR carrier visibility is off for this
    //                          customer (maskOrderCarrier nulls out carrierId when
    //                          carrierNames=false) → we present WAL as the carrier.
    //   isUnAssignedCarrier  → a carrierId exists but the carrier record didn't come back
    //                          (non-onboarded / manual carrier) → generic "Unassigned Carrier".
    const isNoCarrierAssigned = !carrierPrice || !carrierPrice.carrierId;
    const isUnAssignedCarrier = !!(carrierPrice && carrierPrice.carrierId && !carrier);
    // Support both pre-concatenated image and separate imageUrl+image patterns
    const carrierImageSrc = carrier?.imageUrl && carrier?.image ? `${carrier.imageUrl}${carrier.image}` : carrier?.imageUrl || carrier?.image || null;
    // Same field/format as the Overview tab's progress card (OrderStatus/index.tsx) — keep in sync.
    // Digits only under a "MC #" label — same helper/format as OrderStatus and WALHQ.
    const mcId = extractMcNumber(carrier?.mcId);
    const orgMcNumber = extractMcNumber(orgBranding.mcNumber);
    const legId = isMasterTab ? order?.orderId || null : currentLegData?.legId || null;

    const getCarrierDisplayName = () => {
        if (isNoCarrierAssigned) return orgBranding.name;
        if (isUnAssignedCarrier) return t('Common.Unassigned_Carrier');

        return carrier?.name;
    };

    // ── Sales price (right panel) ────────────────────────────────────────────
    const salesPrice = order?.salesPrice;
    const isCad = !salesPrice?.currency || salesPrice?.currency?.toUpperCase() === CURRENCY.CAD;
    const currency = isCad ? CURRENCY.CAD : CURRENCY.USD;

    const baseRate = isCad ? salesPrice?.baseSalePrice : salesPrice?.baseSalePriceUSD;
    const totalCost = isCad ? salesPrice?.totalSalePrice : salesPrice?.totalSalePriceUSD;
    const fuelPct = salesPrice?.fuelPercentage ?? 0;

    const additionalCharges: any[] = Array.isArray(salesPrice?.additional_charge) ? salesPrice.additional_charge : [];
    const additionalTotal = additionalCharges.reduce((sum, c) => sum + Number((isCad ? c.price : c.priceUSD) || 0), 0);
    const hasAdditionals = additionalCharges.length > 0;

    const getChargeLabel = (charge: any): string => {
        if (charge?.isOtherCharge) return charge?.otherCharge || charge?.charge || '-';

        if (charge?.charge && servicesList?.length > 0) {
            return servicesList.find((s: any) => s.value === charge.charge)?.label || charge.charge;
        }

        return charge?.charge || '-';
    };

    // Strip ".00" so whole-number amounts display without decimals (e.g. "$5" not "$5.00").
    // Non-zero decimals like ".50" or ".01" are preserved as-is.
    const formatValue = (val: any): string => {
        const num = Math.round(Number(val ?? 0) * 100) / 100;

        return Number.isInteger(num) ? getFormattedNumber(num, false) : getFormattedNumber(num, true);
    };

    const formatAmount = (val: any) => `$${formatValue(val)} ${currency}`;

    const openCharges = () => {
        if (!hasAdditionals || !additionalsRef.current) return;
        if (chargesCloseTimer.current) clearTimeout(chargesCloseTimer.current);
        const rect = additionalsRef.current.getBoundingClientRect();
        const estimatedHeight = 44 + additionalCharges.length * 24;
        setChargesAbove(window.innerHeight - rect.bottom < estimatedHeight);
        setChargesRect({ bottom: rect.bottom, top: rect.top, left: rect.left });
        setShowCharges(true);
    };

    const closeCharges = () => {
        chargesCloseTimer.current = setTimeout(() => setShowCharges(false), 80);
    };

    const keepChargesOpen = () => {
        if (chargesCloseTimer.current) clearTimeout(chargesCloseTimer.current);
    };

    if (!loading && !carrierPrice && !salesPrice) return null;

    return (
        <CardCmp headerTitle={t('Common.Carrier_Pricing_Details')} headerParentClassName="!rounded-t-lg !bg-transparent" childrenClassName="py-3 flex gap-x-4 w-full overflow-auto scrollbar-hide">
            <div className="shrink-0 max-lg:flex-col flex gap-x-4 w-full">
                {/* ── Left: carrier identity + contact ─────────────────────── */}
                <div className="w-full lg:w-[calc(51%-8px)] min-w-0">
                    {/* Logo + name row */}
                    <div className="flex items-center gap-x-2.5 border-b border-gray100 pb-3">
                        {loading ? (
                            <div className="w-[30px] lg:w-[42px] h-[30px] lg:h-[42px] rounded-md border bg-utilityGray100 flex-none custom-loading" />
                        ) : isNoCarrierAssigned ? (
                            // Org identity (appearance table) — uploaded brand logo, black WAL icon as fallback
                            orgBranding.logoSrc ? (
                                <img src={orgBranding.logoSrc} alt={orgBranding.name} className="w-[30px] lg:w-[42px] h-[30px] lg:h-[42px] rounded-md border bg-white object-contain flex-none" />
                            ) : (
                                <div className="w-[30px] lg:w-[42px] h-[30px] lg:h-[42px] rounded-md bg-utilityBlack flex items-center justify-center p-1.5 flex-none">
                                    <img src={WalLogoIcon} alt={orgBranding.name} className="w-full h-full object-contain" />
                                </div>
                            )
                        ) : isUnAssignedCarrier ? (
                            <div className="w-[30px] lg:w-[42px] h-[30px] lg:h-[42px] rounded-md bg-utilityGray100 flex items-center justify-center p-1.5 flex-none">
                                <Truck01 className="w-full h-full text-gray500" />
                            </div>
                        ) : carrierImageSrc ? (
                            <figure className="flex-none">
                                <img
                                    src={carrierImageSrc}
                                    alt={carrier?.name}
                                    onError={onError}
                                    className="w-[30px] lg:w-[42px] h-[30px] lg:h-[42px] rounded-md border bg-utilityGray100 object-contain"
                                />
                            </figure>
                        ) : (
                            <div className="w-[30px] lg:w-[42px] h-[30px] lg:h-[42px] rounded-md border bg-utilityGray100 flex items-center justify-center flex-none">
                                <p className="text-primary text-base font-semibold">{getShortName(carrier?.name || t('Common.Unassigned_Carrier'))?.toUpperCase()}</p>
                            </div>
                        )}

                        <div className="min-w-0">
                            <p className={`text-grayLight900 text-sm font-medium truncate ${loading ? 'custom-loading' : ''}`}>{loading ? 'lorem ipsum' : getCarrierDisplayName()}</p>
                            {/* Org-presented carrier shows the org MC number (Organization Settings); leg id only when MC isn't configured */}
                            {!loading &&
                                (isNoCarrierAssigned ? (
                                    orgMcNumber ? (
                                        <p className="text-grayLight600 text-xs leading-[18px] font-normal">MC #{orgMcNumber}</p>
                                    ) : (
                                        legId && <p className="text-grayLight600 text-xs leading-[18px] font-normal">#{legId}</p>
                                    )
                                ) : isUnAssignedCarrier ? (
                                    legId && <p className="text-grayLight600 text-xs leading-[18px] font-normal">#{legId}</p>
                                ) : (
                                    mcId && <p className="text-grayLight600 text-xs leading-[18px] font-normal">MC #{mcId}</p>
                                ))}
                        </div>
                    </div>

                    {/* Sales rep contact info — two-column layout */}
                    <div className="pt-3 flex flex-col w-full space-y-1.5">
                        {/* Row 1: Contact Name | Contact Number */}
                        <div className="flex items-stretch w-full gap-x-2 max-lg:flex-col max-lg:gap-y-1.5">
                            <div className="flex items-center gap-x-1 min-w-0 flex-1">
                                <p className="text-grayLight600 text-xs leading-[18px] font-normal whitespace-nowrap">{t('Common.Contact_Name')}:</p>
                                <TruncateTextCmp message={loading ? '' : salesRep?.salesrepname || '-'} refName={nameRef} tooltipType="action">
                                    <p ref={nameRef} className={`text-grayLight900 text-xs leading-[18px] font-medium truncate ${loading ? 'custom-loading w-20' : ''}`}>
                                        {loading ? 'lorem ipsum' : salesRep?.salesrepname || '-'}
                                    </p>
                                </TruncateTextCmp>
                            </div>
                            <div className="w-px bg-gray100 self-stretch max-lg:hidden" />
                            {/* Contact Number */}
                            <div className="flex items-center gap-x-1 min-w-0 flex-1">
                                <p className="text-grayLight600 text-xs leading-[18px] font-normal whitespace-nowrap text-nowrap">{t('Common.Contact_Number')}:</p>
                                <div className={`flex items-center gap-x-0.5 min-w-0 ${loading ? 'custom-loading' : ''}`}>
                                    <TruncateTextCmp message={loading ? '' : salesRep?.phone ? getFormatPhoneWithPlus(salesRep.phone) : '-'} refName={phoneRef} tooltipType="action">
                                        <p ref={phoneRef} className="text-grayLight900 text-xs leading-[18px] font-medium truncate">
                                            {loading ? 'lorem ipsum' : salesRep?.phone ? getFormatPhoneWithPlus(salesRep.phone) : '-'}
                                        </p>
                                    </TruncateTextCmp>
                                    {!loading && salesRep?.phone && (
                                        <TooltipCmp message={t('Common.Call')} childrenClassName="!flex">
                                            <ButtonCmp className="btn-link-outline-primary flex-none" size="none" loading={false} onClick={handleCopyPhone} icon={<PhoneCall01 className="w-4 h-4" />}>
                                                <></>
                                            </ButtonCmp>
                                        </TooltipCmp>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Row 2: Email Address (left) | Contact Number (right) */}
                        <div className="flex items-stretch w-full gap-x-3">
                            {/* Email */}
                            <div className="flex items-center gap-x-1 min-w-0 flex-1">
                                <p className="text-grayLight600 text-xs leading-[18px] font-normal text-nowrap">{t('Common.Email_Address')}:</p>
                                <div className={`flex items-center gap-x-1 min-w-0 ${loading ? 'custom-loading' : ''}`}>
                                    <TruncateTextCmp message={loading ? '' : salesRep?.salesrepemail || '-'} refName={emailRef} tooltipType="action">
                                        <p ref={emailRef} className="text-grayLight900 text-xs leading-[18px] font-medium truncate">
                                            {loading ? 'lorem@ipsum.com' : salesRep?.salesrepemail || '-'}
                                        </p>
                                    </TruncateTextCmp>
                                    {!loading && salesRep?.salesrepemail && (
                                        <TooltipCmp message={t('Common.Email')} childrenClassName="!flex">
                                            <ButtonCmp className="btn-link-outline-primary flex-none" size="none" loading={false} onClick={handleCopyEmail} icon={<Mail01 className="w-4 h-4" />}>
                                                <></>
                                            </ButtonCmp>
                                        </TooltipCmp>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Vertical divider ─────────────────────────────────────── */}
                <div className="w-full max-lg:my-2 max-lg:h-px lg:w-px bg-gray100" />

                {/* ── Right: sales price breakdown ─────────────────────────── */}
                {isReadOnly ? (
                    <div className="w-full lg:w-[calc(49%-8px)] flex-none relative overflow-hidden rounded-lg">
                        {/* Blurred dummy pricing */}
                        <div className="flex flex-col blur-sm pointer-events-none select-none" aria-hidden="true">
                            <div className="flex items-center justify-between pb-1.5 border-b border-gray100">
                                <p className="text-gray500 text-xs leading-[18px] font-medium">Base Price</p>
                                <p className="text-gray500 text-xs leading-[18px] font-medium">$00.00 CAD</p>
                            </div>
                            <div className="flex items-center justify-between py-1.5 border-b border-gray100">
                                <p className="text-gray500 text-xs leading-[18px] font-medium">Additionals</p>
                                <p className="text-gray500 text-xs leading-[18px] font-medium">$00 CAD</p>
                            </div>
                            <div className="flex items-center justify-between py-1.5 border-b border-gray100">
                                <p className="text-gray500 text-xs leading-[18px] font-medium">Fuel</p>
                                <p className="text-gray500 text-xs leading-[18px] font-medium">0.00%</p>
                            </div>
                            <div className="flex items-center justify-between pt-1.5">
                                <p className="text-gray500 text-xs leading-[18px] font-medium">Total Cost</p>
                                <p className="text-gray500 text-xs leading-[18px] font-medium">$00.00 CAD</p>
                            </div>
                        </div>

                        {/* Login overlay */}
                        <div className="absolute inset-0 flex flex-col items-center justify-center  z-10 rounded-lg">
                            <NotFoundUI
                                icon={<Lock01 className="w-5 h-5 text-primary" />}
                                iconClassName="!bg-primary/10 !border-primary/20 !rounded-full !shadow-none !hidden"
                                title="Login to view pricing"
                                desc="Sign in to access shipment pricing details"
                                containerClassName="!my-0 !mx-0"
                                descClass="!pb-0"
                                buttonText="Login to access"
                                buttonType="btn-outline-primary"
                                handleActionType={() => (window.location.href = ROUTES.LOGIN)}
                                isEmptyBackground={true}
                            />
                        </div>
                    </div>
                ) : (
                    <div className="w-full lg:w-[calc(49%-8px)] flex-none flex flex-col">
                        {/* Base Price */}
                        <div className="flex items-center justify-between pb-1.5 border-b border-gray100">
                            <p className={`text-gray500 text-xs leading-[18px] font-medium ${loading ? 'custom-loading' : ''}`}>{loading ? 'lorem ipsum' : t('Common.Base_Price')}</p>
                            <p className={`text-grayLight900 text-xs leading-[18px] font-medium ${loading ? 'custom-loading' : ''}`}>{loading ? '$0.00 CAD' : formatAmount(baseRate)}</p>
                        </div>

                        {/* Additionals row */}
                        <div className="flex items-center justify-between py-1.5 border-b border-gray100">
                            {loading ? (
                                <p className="text-gray500 text-xs leading-[18px] font-medium custom-loading">lorem ipsum</p>
                            ) : (
                                <div className="leading-[18px] flex" onMouseEnter={openCharges} onMouseLeave={closeCharges}>
                                    <span ref={additionalsRef} className={`text-gray500 text-xs leading-[18px] font-medium ${hasAdditionals ? 'border-dot-common cursor-pointer' : ''}`}>
                                        {t('Common.Additionals')}
                                    </span>
                                </div>
                            )}
                            <p className={`text-grayLight900 text-xs leading-[18px] font-medium ${loading ? 'custom-loading' : ''}`}>{loading ? '$0.00 CAD' : formatAmount(additionalTotal)}</p>
                        </div>

                        {/* Portal: renders at document.body to escape the overflow-auto container,
                            positioned with fixed coords from getBoundingClientRect so it floats
                            above/below the trigger without being clipped by any parent. */}
                        {showCharges &&
                            hasAdditionals &&
                            chargesRect &&
                            ReactDOM.createPortal(
                                <div
                                    style={{
                                        position: 'fixed',
                                        zIndex: 9999,
                                        left: chargesRect.left,
                                        ...(chargesAbove ? { bottom: window.innerHeight - chargesRect.top + 4 } : { top: chargesRect.bottom + 4 }),
                                    }}
                                    className="bg-white border border-utilityGray200 rounded-lg shadow text-xs min-w-[280px]"
                                    onMouseEnter={keepChargesOpen}
                                    onMouseLeave={closeCharges}
                                >
                                    <div className="flex flex-col py-1.5 px-2 text-left">
                                        <p className="text-xs font-medium text-grayLight900 mb-1">Additional Charges</p>
                                        {additionalCharges.map((charge, idx) => (
                                            <div key={charge.id ?? idx} className="flex items-center justify-between gap-x-3 py-0.5">
                                                <ChargeLabelCell label={getChargeLabel(charge)} />
                                                <p className="text-xs text-grayLight900 font-medium flex-none">{formatAmount(isCad ? charge.price : charge.priceUSD)}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>,
                                document.body
                            )}

                        {/* Fuel */}
                        <div className="flex items-center justify-between py-1.5 border-b border-gray100">
                            <p className={`text-gray500 text-xs leading-[18px] font-medium ${loading ? 'custom-loading' : ''}`}>{loading ? 'lorem ipsum' : t('Common.Fuel')}</p>
                            <p className={`text-grayLight900 text-xs leading-[18px] font-medium ${loading ? 'custom-loading' : ''}`}>{loading ? '0%' : `${formatValue(fuelPct)}%`}</p>
                        </div>

                        {/* Total Sale Price */}
                        <div className="flex items-center justify-between pt-1.5">
                            <p className={`text-gray500 text-xs leading-[18px] font-medium ${loading ? 'custom-loading' : ''}`}>{loading ? 'lorem ipsum' : t('Common.Total_Cost')}</p>
                            <p className={`text-grayLight900 text-xs leading-[18px] font-semibold ${loading ? 'custom-loading' : ''}`}>{loading ? '$0.00 CAD' : formatAmount(totalCost)}</p>
                        </div>
                    </div>
                )}
            </div>
        </CardCmp>
    );
};

export default CarrierAssignment;
